/**
 * ==========================================================================
 *  FeedbackWidget.jsx — Helpdesk-Widget fuer das Pflege-Cockpit
 * ==========================================================================
 *  Floating-Button unten rechts; Klick oeffnet ein Panel mit:
 *    - Begruessung "Hallo!" + Portraitfoto
 *    - Auswahl eines Bereichs (Pflege-Cockpit, Pflegegrad-Rechner, ...)
 *    - Textfeld + Name + Email/Telefon + Senden-Button
 *    - Aufklappbarer "Tutorials"-Bereich mit 4 PDF-Verlinkungen
 *    - Datenschutz/Impressum-Links unten
 *
 *  Wird auf zwei Wegen genutzt:
 *    1. /feedback-widget — standalone, fuer Iframe-Embedding in
 *       app.pflege-dschungel.de (Niko baut den Iframe-Tag ein).
 *    2. Direkt eingebettet in /feedback-demo, damit Hendrik ohne
 *       Iframe-Setup testen kann.
 *
 *  Iframe-Resize:
 *    Wenn das Widget zu/auf wechselt, wird per postMessage an
 *    window.parent das Event 'feedback-widget-resize' geschickt mit
 *    den Maessen, die der Iframe annehmen soll. Der Embed-Snippet
 *    in der parent-Page hoert das ab und passt Width/Height an.
 *
 *  DSGVO:
 *    - Keine aktive Haekchen-Bestaetigung — eingeloggte Cockpit-Nutzer
 *      haben dem Verfahren bereits beim Login zugestimmt.
 *    - Datenschutz + Impressum als Links unten zur Information.
 * ==========================================================================
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { api, ApiError } from '../api';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0' };

const PORTRAIT_URL = '/portrait-dohmeyer.png';
const DATENSCHUTZ_URL = 'https://pflege-dschungel.de/datenschutz';
const IMPRESSUM_URL = 'https://pflege-dschungel.de/impressum';

const BEREICHE = [
  { key: 'pflegecockpit', label: 'Pflege-Cockpit (Allgemeine Frage)' },
  { key: 'pflegegradrechner', label: 'Pflegegrad-Rechner' },
  { key: 'verhinderungspflege', label: 'Verhinderungspflege-Rechner' },
  { key: 'nachbarschaftshilfe', label: 'Nachbarschaftshilfe-Rechner' },
  { key: 'liane_pflegebox', label: 'Liane Pflegebox' },
];

const TUTORIALS = [
  {
    titel: 'Team einrichten',
    url: 'https://pflege-dschungel.de/wp-content/uploads/2026/04/Pflege-Cockpit-Tutorial-Team.pdf',
  },
  {
    titel: 'Pflegegrad-Rechner',
    url: 'https://pflege-dschungel.de/wp-content/uploads/2026/04/Pflege-Cockpit-Tutorial-Pflegegrad-Rechner.pdf',
  },
  {
    titel: 'VHP-Rechner',
    url: 'https://pflege-dschungel.de/wp-content/uploads/2026/04/Pflege-Cockpit-Tutorial-VHP-Rechner.pdf',
  },
  {
    titel: 'NBH-Rechner',
    url: 'https://pflege-dschungel.de/wp-content/uploads/2026/04/Pflege-Cockpit-Tutorial-NBH-Rechner.pdf',
  },
];

export default function FeedbackWidget() {
  // URL-Parameter beim Iframe-Start lesen — wenn die Pflege-Cockpit-App die
  // Nutzer-Daten beim Embedden mitgibt (Variante 2), sind Name/Email/Telefon
  // direkt vorausgefuellt. Sonst leer.
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialName = urlParams.get('name') || '';
  const initialEmail = urlParams.get('email') || '';
  const initialTelefon = urlParams.get('phone') || urlParams.get('telefon') || '';

  const [offen, setOffen] = useState(false);
  const [bereich, setBereich] = useState('');
  const [text, setText] = useState('');
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [telefon, setTelefon] = useState(initialTelefon);
  const [tutorialsOffen, setTutorialsOffen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [erfolg, setErfolg] = useState(false);

  // Iframe-Parent informieren, wenn sich der Auf/Zu-Status aendert.
  useEffect(() => {
    try {
      window.parent?.postMessage({
        type: 'feedback-widget-resize',
        geoeffnet: offen,
      }, '*');
    } catch { /* parent kann nicht erreicht werden — egal, dann sind wir
                  standalone in einem eigenen Tab */ }
  }, [offen]);

  const reset = () => {
    setBereich('');
    setText('');
    // Name/Email/Telefon zuruecksetzen auf die initialen URL-Param-Werte
    // (in Variante 2 bleiben sie also vorausgefuellt, in Variante 1 leer).
    setName(initialName);
    setEmail(initialEmail);
    setTelefon(initialTelefon);
    setTutorialsOffen(false);
    setFehler(null);
    setErfolg(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFehler(null);
    if (!bereich) {
      setFehler('Bitte einen Bereich auswählen.');
      return;
    }
    if (!text.trim()) {
      setFehler('Bitte eine Nachricht eintragen.');
      return;
    }
    if (!email.trim() && !telefon.trim()) {
      setFehler('Bitte E-Mail oder Telefonnummer angeben — sonst können wir uns nicht zurückmelden.');
      return;
    }
    if (email && !email.includes('@')) {
      setFehler('Die E-Mail-Adresse sieht nicht gültig aus.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/feedback', {
        bereich,
        name: name.trim() || null,
        email: email.trim() || null,
        telefon: telefon.trim() || null,
        text: text.trim(),
      });
      setErfolg(true);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status}`)
        : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOffen(false);
    // Nach Erfolg auch Form zuruecksetzen — naechstes Mal frisch starten.
    if (erfolg) reset();
  };

  // ============================================================
  // Floating-Button (Widget zu)
  // ============================================================
  if (!offen) {
    return (
      <div style={containerStyle}>
        <button
          onClick={() => setOffen(true)}
          aria-label="Hilfe und Feedback"
          style={floatingButtonStyle}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1.0)'}
        >
          {/* Chat-Bubble-Icon, inline-SVG damit's keine externe Abhaengigkeit gibt */}
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </button>
      </div>
    );
  }

  // ============================================================
  // Aufgeklapptes Panel
  // ============================================================
  return (
    <div style={containerStyle}>
      <div style={panelStyle}>
        {/* Header mit Brombeer-Hintergrund + Foto + Schliess-X */}
        <div style={headerStyle}>
          <button onClick={handleClose} aria-label="Schließen" style={closeButtonStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <div style={portraitWrapperStyle}>
            <img src={PORTRAIT_URL} alt="Hendrik Dohmeyer" style={portraitImgStyle} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </div>
          <h2 style={headerTitleStyle}>Hallo! 👋</h2>
          <p style={headerSubtitleStyle}>
            Ich bin Hendrik. Schön, dass Sie hier sind — sagen Sie mir, wo es hakt oder
            was Sie sich wünschen.
          </p>
        </div>

        {/* Body: scrollbar */}
        <div style={bodyStyle}>
          {erfolg ? (
            <div style={erfolgStyle}>
              <div style={erfolgIconStyle}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1f2937', margin: '16px 0 8px' }}>Vielen Dank!</h3>
              <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                Ihre Nachricht ist angekommen. Wir melden uns auf dem von Ihnen
                gewählten Kanal {email && telefon ? '(per E-Mail oder telefonisch) ' : email ? '(per E-Mail) ' : '(telefonisch) '}
                so schnell wie möglich bei Ihnen zurück.
              </p>
              <button
                onClick={() => { reset(); }}
                style={{ ...primaryButtonStyle, marginTop: 20 }}
              >
                Weitere Nachricht senden
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Bereich-Auswahl */}
              <div>
                <label style={labelStyle}>Worum geht es?</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {BEREICHE.map(b => (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => setBereich(b.key)}
                      style={{
                        ...bereichButtonStyle,
                        ...(bereich === b.key ? bereichButtonAktivStyle : {}),
                      }}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nachrichtentext + Kontakt — erst sichtbar nach Bereich-Auswahl */}
              {bereich && (
                <>
                  <div>
                    <label style={labelStyle}>Ihre Nachricht</label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Beschreiben Sie kurz, was Sie auf dem Herzen haben…"
                      rows={4}
                      maxLength={5000}
                      style={textareaStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Ihr Name (optional)</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Vor- und Nachname"
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>E-Mail</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ihre@adresse.de"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>oder Telefon</label>
                      <input
                        type="tel"
                        value={telefon}
                        onChange={(e) => setTelefon(e.target.value)}
                        placeholder="0151 12345678"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {fehler && (
                    <div style={fehlerBoxStyle}>{fehler}</div>
                  )}

                  <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                    {submitting ? 'Wird gesendet…' : 'Nachricht senden'}
                  </button>
                </>
              )}

              {/* Tutorials-Aufklapp-Bereich */}
              <div style={{ marginTop: 8, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setTutorialsOffen(v => !v)}
                  style={tutorialToggleStyle}
                >
                  <span>📚 Tutorials zum Pflege-Cockpit</span>
                  <span style={{ fontSize: 18 }}>{tutorialsOffen ? '−' : '+'}</span>
                </button>
                {tutorialsOffen && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                    {TUTORIALS.map(t => (
                      <a
                        key={t.url}
                        href={t.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={tutorialCardStyle}
                      >
                        <div style={tutorialCardIconStyle}>📄</div>
                        <div style={tutorialCardTitleStyle}>{t.titel}</div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer mit Datenschutz/Impressum */}
        <div style={footerStyle}>
          <a href={DATENSCHUTZ_URL} target="_blank" rel="noopener noreferrer" style={footerLinkStyle}>
            Datenschutz
          </a>
          <span style={{ color: '#d1d5db' }}>·</span>
          <a href={IMPRESSUM_URL} target="_blank" rel="noopener noreferrer" style={footerLinkStyle}>
            Impressum
          </a>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Inline-Styles — bewusst keine Tailwind-Klassen, damit das Widget auch
// ohne unsere Tailwind-Config in einem fremden Iframe-Kontext sauber aussieht.
// (Im Browser-Tab wird Tailwind eh geladen, aber so ist's idiotensicher.)
// ==========================================================================

const containerStyle = {
  position: 'fixed',
  bottom: 20,
  right: 20,
  zIndex: 999999,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const floatingButtonStyle = {
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: CI.brombeer,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 16px rgba(133, 54, 108, 0.35)',
  transition: 'transform 0.15s ease',
  padding: 0,
};

const panelStyle = {
  width: 380,
  maxHeight: '80vh',
  backgroundColor: 'white',
  borderRadius: 16,
  boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  position: 'relative',
  background: `linear-gradient(135deg, ${CI.brombeer} 0%, #6e2a59 100%)`,
  padding: '24px 20px 20px',
  textAlign: 'center',
  color: 'white',
};

const closeButtonStyle = {
  position: 'absolute',
  top: 12,
  right: 12,
  width: 30,
  height: 30,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.15)',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const portraitWrapperStyle = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.2)',
  margin: '0 auto 12px',
  overflow: 'hidden',
  border: '3px solid rgba(255,255,255,0.4)',
};

const portraitImgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const headerTitleStyle = {
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
};

const headerSubtitleStyle = {
  margin: '6px 0 0',
  fontSize: 13,
  lineHeight: 1.5,
  opacity: 0.95,
};

const bodyStyle = {
  padding: 18,
  overflowY: 'auto',
  flex: 1,
};

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const textareaStyle = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: 70,
  fontFamily: 'inherit',
};

const bereichButtonStyle = {
  width: '100%',
  padding: '10px 12px',
  textAlign: 'left',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  fontSize: 13,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  color: '#374151',
  fontFamily: 'inherit',
};

const bereichButtonAktivStyle = {
  backgroundColor: CI.brombeer,
  color: 'white',
  borderColor: CI.brombeer,
  fontWeight: 600,
};

const primaryButtonStyle = {
  width: '100%',
  padding: '12px 16px',
  backgroundColor: CI.brombeer,
  color: 'white',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'opacity 0.15s ease',
};

const fehlerBoxStyle = {
  padding: '8px 12px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 8,
  fontSize: 12,
  color: '#991b1b',
};

const erfolgStyle = {
  textAlign: 'center',
  padding: '20px 10px',
};

const erfolgIconStyle = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#10b981',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
};

const tutorialToggleStyle = {
  width: '100%',
  padding: '8px 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  fontFamily: 'inherit',
};

const tutorialCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 10,
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  textDecoration: 'none',
  color: '#374151',
  fontSize: 12,
  transition: 'all 0.15s ease',
};

const tutorialCardIconStyle = {
  fontSize: 22,
  flexShrink: 0,
};

const tutorialCardTitleStyle = {
  fontWeight: 600,
  lineHeight: 1.3,
};

const footerStyle = {
  padding: '10px 18px',
  borderTop: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
  fontSize: 11,
};

const footerLinkStyle = {
  color: '#6b7280',
  textDecoration: 'none',
};
